// src/components/shopping/ShoppingListPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  List,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Container,
  MenuItem,
  Checkbox,
  Tooltip,
} from "@mui/material";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../services/api";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Grid from "@mui/material/Grid";
import ShoppingListItemRow from "./ShoppingListItemRow";
import InstacartCheckoutButton from "./modal/InstacartCheckoutButton";
import FeatureGateDialog from "../modals/FeatureGateDialog";
import { userHasActiveEntitlement } from "../../utils/entitlements";
import { useLocationCtx } from "../../context/LocationContext";
import { resolveCountryId } from "../../utils/country";
import { useCountryGate } from "../../hooks/useCountryGate";
import { withCountryPrefix, withQuery } from "../utils/routeHelpers";
import PriceHistoryDialog from "./PriceHistoryDialog";


function getCurrencySymbol(countryId) {
  const c = countryId || "1";
  return c === "2" || c === 2 ? "$" : "€";
}

function buildSnapshotFromTotals(totals) {
  if (!totals) return null;
  return {
    theoretical_total: Number(totals.theoretical_total ?? totals.theoretical_cost ?? 0),
    expected_shopping_total: Number(
      totals.expected_shopping_total ?? totals.basket_total_cost ?? totals.basket_cost ?? 0
    ),
  };
}

function formatUnitPriceForCountry(
  unitPricePerKgOrL,
  country,
  isLiquid,
  isPiece,
  currencySymbol
) {
  const value = Number(unitPricePerKgOrL || 0);
  if (!value) return `-- ${currencySymbol}`;

  if (isPiece) {
    return `${value.toFixed(2)}${currencySymbol} / piece`;
  }

  // US
  if (country === "2" || country === 2) {
    if (isLiquid) {
      const pricePerFlOz = value / 33.814;
      return `${pricePerFlOz.toFixed(2)}${currencySymbol} / fl oz`;
    } else {
      const pricePerLb = value / 2.20462;
      return `${pricePerLb.toFixed(2)}${currencySymbol} / lb`;
    }
  }

  // Ευρώπη / default: €/kg ή €/L
  return isLiquid
    ? `${value.toFixed(2)}${currencySymbol} / L`
    : `${value.toFixed(2)}${currencySymbol} / kg`;
}

const BLOCKING_MISSING_PRICING_REASON_CODES = new Set([
  "missing_variant",
  "missing_price",
  "missing_package_size",
  "missing_unit_price",
]);

export function countTrulyNonPricedItems(items = []) {
  return (Array.isArray(items) ? items : []).filter((item) => {
    if (item?.include_in_totals !== false) return false;
    const reasonCode = String(item?.reason_code || item?.pricing_snapshot?.reason || "").trim().toLowerCase();
    return BLOCKING_MISSING_PRICING_REASON_CODES.has(reasonCode);
  }).length;
}

export function resolveSelectedStoreFromListPayload(payload, fallback = "ALL") {
  return (
    payload?.list?.selected_store ||
    payload?.list?.store_location_id ||
    fallback
  );
}

function formatQuantity(qty, country, unit = "g", lang = "en") {
  const n = Number(qty || 0);
  const rawUnit = String(unit || "g").toLowerCase();
  const normalizedUnit = rawUnit === "count" ? "piece" : rawUnit;

  if (normalizedUnit === "piece") {
    return `${n.toFixed(0)} ${lang === "el" || lang === "gr" ? "τεμ." : n === 1 ? "pc" : "pcs"}`;
  }

  if (normalizedUnit === "ml") {
    if (country === "2" || country === 2) {
      const flOz = n / 29.5735295625;
      return `${flOz.toFixed(1)} fl oz`;
    }
    if (n >= 1000) {
      return `${(n / 1000).toFixed(2)} L`;
    }
    return `${n.toFixed(0)} ml`;
  }

  if (country === "2" || country === 2) {
    if (n >= 453.6) {
      const lbs = n / 453.6;
      return `${lbs.toFixed(2)} lb`;
    } else {
      const oz = n / 28.35;
      return `${oz.toFixed(1)} oz`;
    }
  }

  if (n >= 1000) {
    return `${(n / 1000).toFixed(2)} kg`;
  }
  return `${n.toFixed(0)} g`;
}

function buildDisplayItems(items, showAll, userIngredients, lang) {
  return items.map((item) => {
    const pricing = item.pricing_snapshot || {};
    const requiredQty = Number(
      pricing.required_qty ??
      item.required_qty ??
      item.required_quantity ??
      item.qty ??
      0
    );
    const requiredUnitRaw = pricing.required_unit || item.required_unit || item.base_unit || "g";
    const requiredUnit = String(requiredUnitRaw || "g").toLowerCase() === "count" ? "piece" : requiredUnitRaw;

    // Eιδική περίπτωση: προϊόν που δεν βρέθηκε
    if (item.not_found) {
      return {
        ...item,
        displayQty: requiredQty,
        required_qty: requiredQty,
        required_unit: requiredUnit,
        userHasQty: 0,
        needQty: requiredQty,
      };
    }

    const productName =
      lang === "el" || lang === "gr"
        ? item.product_name_el || item.name_el || item.name
        : item.product_name_en || item.name_en || item.name;

    const needQty = requiredQty;

    // Προσπάθεια ταύτισης με αποθήκη χρήστη
    let entry = null;
    if (item.generic_product_id) {
      entry = userIngredients.find(
        (ui) =>
          ui.generic_product_id &&
          String(ui.generic_product_id) === String(item.generic_product_id)
      );
    }
    if (!entry && productName) {
      entry = userIngredients.find(
        (ui) =>
          ui.name &&
          ui.name.trim().toLowerCase() === productName.trim().toLowerCase()
      );
    }

    const userHasQty = entry ? Number(entry.qty || 0) : 0;
    let displayQty = needQty;

    if (!showAll) {
      displayQty = Math.max(needQty - userHasQty, 0);
    }

    return {
      ...item,
      pricing_snapshot: pricing,
      required_qty: requiredQty,
      required_unit: requiredUnit,
      displayQty,
      calculated_cost: item.purchase_cost ?? pricing.purchase_cost ?? null,
      theoretical_cost: item.consumption_cost ?? pricing.consumption_cost ?? null,
      basket_cost: item.purchase_cost ?? pricing.purchase_cost ?? null,
      userHasQty,
      needQty,
    };
  });
}

// Payload για /recalculate
function buildRecalcPayloadItems(rawItems) {
  return rawItems
    .filter((it) => it.generic_product_id)
    .map((it) => {
      const required = Number(
        it.required_quantity || it.needQty || it.qty || 0
      );
      return {
        generic_product_id: it.generic_product_id,
        required_quantity: required,
        current_variant_id:
          it.product_variant_id || it.store_product_id || null,
      };
    });
}

export default function ShoppingListPage() {
  const [baseItems, setBaseItems] = useState([]);
  const [rawItems, setRawItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [entitlements, setEntitlements] = useState(null);
  const [gateOpen, setGateOpen] = useState(false);
  const [members, setMembers] = useState(1);
  const [userIngredients, setUserIngredients] = useState([]);
  const [showAll, setShowAll] = useState(true);
  const [gateRequire, setGateRequire] = useState("auth"); // "auth" | "premium"
  const [gateMessage, setGateMessage] = useState("");


  const [selectedStore, setSelectedStore] = useState("ALL");
  const [selectedIds, setSelectedIds] = useState([]); // για Instacart checkout
  const [authChecked, setAuthChecked] = useState(false);
  const [priceChangesByVariant, setPriceChangesByVariant] = useState({});
  const [historyItem, setHistoryItem] = useState(null);
  const [watchlistNotice, setWatchlistNotice] = useState("");
  const [followedByGenericId, setFollowedByGenericId] = useState({});
  const [basketSavings, setBasketSavings] = useState(null);
  const [costSnapshot, setCostSnapshot] = useState(null);
  const [listTotals, setListTotals] = useState(null);
  const selectedStoreChangeSourceRef = useRef("initial");
  const previousSelectedStoreRef = useRef("ALL");

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const paramsUrl = useParams();

  let lang = paramsUrl.lang;
  if (!lang) lang = i18n.language || "en";
  const apiLang = lang === "el" ? "gr" : lang;
  const { country: ctxCountry } = useLocationCtx();
  const country = resolveCountryId({ locationCountry: ctxCountry }).id;
  const currencySymbol = getCurrencySymbol(country);
  const isUS = country === "2" || country === 2;
  const { needsCountrySelection } = useCountryGate();

  const params = new URLSearchParams(location.search);
  const previewRecipeId = params.get("preview");
  const previewMembers = parseInt(params.get("members")) || 1;
  const isGuestFromMealplan = params.get("guest") === "1";
  const listIdFromQuery = params.get("list_id");
  const mode = params.get("mode");
  const returnTo = `${location.pathname || ""}${location.search || ""}`;
  const loginPath = withQuery(withCountryPrefix("/login", lang), { returnTo });
  const registerPath = withQuery(withCountryPrefix("/register", lang), { returnTo });
  const plansPath = withCountryPrefix("/plans", lang);
  const mealPlanOptimizePath = withQuery(withCountryPrefix("/client/meal/plan", lang), {
    intent: "optimize",
  });

  const isUserListMode =
    isLoggedIn && !previewRecipeId && !isGuestFromMealplan;
  const canUsePremiumFeatures = isLoggedIn && isPremium;
  const usePremiumLayout = !!previewRecipeId || isGuestFromMealplan || isUserListMode;
  const readOnly = !isLoggedIn || !!previewRecipeId;
  const canChangeSelection = !previewRecipeId;


  // διαβάζουμε user_ingredients
  useEffect(() => {
    const saved = localStorage.getItem("user_ingredients");
    setUserIngredients(saved ? JSON.parse(saved) : []);
  }, []);

  // φέρνουμε preview ή λίστα
  const fetchPreview = (recipeId, membersCount, countryId) => {
    if (needsCountrySelection) {
      if (process.env.REACT_APP_DEBUG_GUEST_STATE === "1") {
        console.info("[ShoppingList] previewForRecipe blocked: country not selected");
      }
      return;
    }
    setLoading(true);
    api.shoppingList
      .previewForRecipe(recipeId, membersCount, countryId, apiLang)
      .then((res) => {
        const items = res.data.data || [];
        setBaseItems(items);
        setRawItems(items);
        setListTotals(res?.data?.totals || null);
        // αρχικά επιλεγμένα μόνο Instacart items
        setSelectedIds(buildDefaultSelectedIds(items, isUS));
        setLoading(false);
      })
      .catch(() => {
        setCostSnapshot(null);
        setError(
          t("shopping_list_load_error") ||
          "Σφάλμα κατά την φόρτωση της λίστας αγορών."
        );
        setLoading(false);
      });
  };

  useEffect(() => {
    // Το i18next δεν έχει "gr", έχει "el".
    const uiLang = lang === "gr" ? "el" : lang;

    if (uiLang && i18n.language !== uiLang) {
      i18n.changeLanguage(uiLang);
    }
  }, [lang, i18n]);

  const fetchList = () => {
    if (needsCountrySelection) {
      if (process.env.REACT_APP_DEBUG_GUEST_STATE === "1") {
        console.info("[ShoppingList] list blocked: country not selected");
      }
      return;
    }
    setLoading(true);
    api.shoppingList
      .list(country, apiLang)
      .then((res) => {
        const items = res.data.data || [];
        setBaseItems(items);
        setRawItems(items);
        setCostSnapshot(res?.data?.list?.cost_snapshot_json || buildSnapshotFromTotals(res?.data?.totals));
        setListTotals(res?.data?.totals || null);

        // ✅ αρχικά τσεκαρισμένα Instacart items και για saved lists
        setSelectedIds(buildDefaultSelectedIds(items, isUS));
        setLoading(false);
      })
      .catch(() => {
        setError(
          t("shopping_list_load_error") ||
          "Σφάλμα κατά την φόρτωση της λίστας αγορών."
        );
        setLoading(false);
      });
  };

  const refreshCurrentData = () => {
    if (previewRecipeId) {
      return fetchPreview(previewRecipeId, previewMembers, country);
    }
    if (isGuestFromMealplan) {
      return api.shoppingList
        .getGuestActive({ source: "meal_plan", lang: apiLang })
        .then((res) => {
          const payload = res?.data || {};
          const items = payload.items || payload.data || [];
          setBaseItems(items);
          setRawItems(items);
          setSelectedIds(buildDefaultSelectedIds(items, isUS));
          setCostSnapshot(buildSnapshotFromTotals(payload.totals));
          setListTotals(payload.totals || null);
        });
    }
    if (listIdFromQuery) {
      return api.shoppingList.getById(listIdFromQuery, apiLang).then((res) => {
        const items = res?.data?.data || res?.data?.items || [];
        setBaseItems(items);
        setRawItems(items);
        setCostSnapshot(res?.data?.list?.cost_snapshot_json || buildSnapshotFromTotals(res?.data?.totals));
        setListTotals(res?.data?.totals || null);
        setSelectedIds(buildDefaultSelectedIds(items, isUS));
      });
    }
    return fetchList();
  };

  const buildDefaultSelectedIds = (items, isUS) => {
    const arr = Array.isArray(items) ? items : [];
    if (isUS) {
      return arr
        .filter((it) => it.generic_product_id)
        .map((it) => String(it.generic_product_id));
    }
    return arr
      .filter((it) => it.generic_product_id)
      .map((it) => String(it.generic_product_id)); // ή κράτα τη δική σου old logic
  };




  useEffect(() => {
    let mounted = true;

    // 1) Members (preview members / household members που ήδη χρησιμοποιείς)
    setMembers(previewMembers);

    // 2) Login state (μην μπλοκάρει το flow αν αργήσει)
    Promise.allSettled([api.users.getCurrentUser(), api.users.entitlements()])
      .then(([userResult, entitlementsResult]) => {
        if (!mounted) return;
        if (userResult.status !== "fulfilled") {
          setIsLoggedIn(false);
          setIsPremium(false);
          setEntitlements(null);
          setAuthChecked(true);
          return;
        }
        const res = userResult.value;
        const user = res?.data?.user || res?.data || {};
        const nextEntitlements = entitlementsResult.status === "fulfilled"
          ? entitlementsResult.value?.data?.entitlements || null
          : null;
        const isAuthenticated = Boolean(
          res?.data?.audience?.is_authenticated ||
          res?.data?.is_authenticated
        );
        setIsLoggedIn(isAuthenticated);
        setEntitlements(nextEntitlements);
        setIsPremium(userHasActiveEntitlement({ ...user, entitlements: nextEntitlements }));
        setAuthChecked(true);
      })
      .catch(() => {
        if (!mounted) return;
        setIsLoggedIn(false);
        setIsPremium(false);
        setEntitlements(null);
        setAuthChecked(true);
      });


    // helper: ασφαλές setLoading/setError
    const startLoading = () => {
      if (!mounted) return;
      setError("");
      setLoading(true);
    };

    const stopLoading = () => {
      if (!mounted) return;
      setLoading(false);
    };

    const setLoadError = () => {
      if (!mounted) return;
      setError(
        t("shopping_list_load_error") ||
        "Σφάλμα κατά την φόρτωση της λίστας αγορών."
      );
    };

    // 3) ROUTING LOGIC
    // A) Preview από recipe
    if (previewRecipeId) {
      startLoading();
      Promise.resolve(fetchPreview(previewRecipeId, previewMembers, country))
        .catch(() => setLoadError())
        .finally(() => stopLoading());
    }
    else if (isGuestFromMealplan) {
      startLoading();

      api.shoppingList
        .getGuestActive({ source: "meal_plan", lang: apiLang })
        .then((res) => {
          console.log("GUEST ACTIVE RAW RESPONSE:", res?.data);

          if (!mounted) return;

          const payload = res?.data || {};
          const items = payload.items || payload.data || [];

          if (!Array.isArray(items) || items.length === 0) {
            setError(
              t("shopping_list_load_error") ||
              "Δεν βρέθηκε αποθηκευμένη λίστα. Δημιούργησε ξανά πλάνο."
            );
          setBaseItems([]);
          setRawItems([]);
          setSelectedIds([]);
          setCostSnapshot(null);
          setListTotals(null);
          return;
        }

          setBaseItems(items);
          setRawItems(items);
          setSelectedIds(buildDefaultSelectedIds(items, isUS));
          setCostSnapshot(buildSnapshotFromTotals(payload.totals));
          setListTotals(payload.totals || null);
        })
        .catch(() => {
          if (!mounted) return;
          setLoadError();
        })
        .finally(() => stopLoading());
    }


    // D) Συγκεκριμένη λίστα (με list_id)
    else if (listIdFromQuery) {
      startLoading();

      api.shoppingList
        .getById(listIdFromQuery, apiLang) // ✅ πέρασε και lang αν το API map σου το δέχεται
        .then((res) => {
          if (!mounted) return;

          const items =
            res?.data?.data ||
            res?.data?.items ||
            [];

          setBaseItems(items);
          setRawItems(items);
          setCostSnapshot(res?.data?.list?.cost_snapshot_json || buildSnapshotFromTotals(res?.data?.totals));
          setListTotals(res?.data?.totals || null);
          setSelectedIds(buildDefaultSelectedIds(items, isUS));
        })
        .catch(() => {
          if (!mounted) return;
          setLoadError();
        })
        .finally(() => {
          stopLoading();
        });
    }
    // E) Default: open list
    else {
      if (!isLoggedIn) {
        if (!mounted) return;
          setBaseItems([]);
          setRawItems([]);
          setSelectedIds([]);
          setCostSnapshot(null);
          setListTotals(null);
          stopLoading();
          return;
        }
      startLoading();
      Promise.resolve(fetchList())
        .catch(() => setLoadError())
        .finally(() => stopLoading());
    }

    return () => {
      mounted = false;
    };

    // eslint-disable-next-line
  }, [
    t,
    previewRecipeId,
    previewMembers,
    country,
    apiLang,
    isGuestFromMealplan,
    listIdFromQuery,
    mode, // ✅ ΜΗΝ το ξεχάσεις
  ]);

  const openGateForQuantityUpdate = () => {
    setGateRequire("premium");
    setGateMessage(
      t("premium_required_desc") ||
      "Η ενημέρωση ποσοτήτων στη λίστα είναι διαθέσιμη μόνο για Premium χρήστες."
    );
    setGateOpen(true);
  };

  const openPremiumGate = (message) => {
    setGateRequire("premium");
    setGateMessage(
      message ||
      t("premium_required_desc") ||
      "This feature is available only for Premium users."
    );
    setGateOpen(true);
  };

  const isPremiumBlocked = () => !isPremium || !isLoggedIn;


  // toggle checkbox σε preview/guest mode
  const toggleSelected = (gpId) => {
    if (!gpId) return;
    const id = String(gpId);
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const availableStores = useMemo(() => {
    const map = new Map();
    const source = baseItems && baseItems.length > 0 ? baseItems : rawItems;

    source.forEach((it) => {
      const variant = it.selected_variant || {};
      const chain = variant.store_chain || it.store_chain || it.store;
      const city = variant.store_name || it.store_name || "";
      const storeLocationId = variant.store_location_id || it.store_location_id;

      if (!storeLocationId || (!chain && !city)) return;

      let label = chain || city;
      if (chain && city && chain !== city) {
        label = `${chain} – ${city}`;
      }

      const value = String(storeLocationId);
      if (!map.has(value)) {
        map.set(value, label);
      }
    });

    if (selectedStore && selectedStore !== "ALL" && !map.has(selectedStore)) {
      map.set(selectedStore, selectedStore);
    }

    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [baseItems, rawItems, selectedStore]);

  const displayItems = useMemo(() => {
    const items = buildDisplayItems(rawItems, showAll, userIngredients, lang);
    return items.map((it) => {
      const basketSource =
        it.purchase_cost ?? it.basket_cost ?? it.calculated_cost ?? null;
      const theoreticalSource =
        it.consumption_cost ?? it.theoretical_cost ?? basketSource ?? null;
      const basket = basketSource == null ? null : Number(basketSource);
      const theo = theoreticalSource == null ? null : Number(theoreticalSource);
      return {
        ...it,
        basket_cost: basket == null ? null : Number(basket.toFixed(2)),
        theoretical_cost: theo == null ? null : Number(theo.toFixed(2)),
        calculated_cost: basket == null ? null : Number(basket.toFixed(2)),
      };
    });
  }, [rawItems, showAll, userIngredients, lang]);

  const missingPricingCount = countTrulyNonPricedItems(displayItems);

  useEffect(() => {
    if (process.env.REACT_APP_DEBUG_SHOPPING_STORE !== "1") return;
    if (previousSelectedStoreRef.current === selectedStore) return;
    console.debug("[ShoppingList] selectedStore changed", {
      from: previousSelectedStoreRef.current,
      to: selectedStore,
      source: selectedStoreChangeSourceRef.current || "unknown",
    });
    previousSelectedStoreRef.current = selectedStore;
  }, [selectedStore]);

  const totalBasketCost = displayItems.reduce(
    (acc, i) =>
      acc +
      (i.include_in_totals
        ? (Number(i.purchase_cost ?? i.basket_cost ?? i.calculated_cost ?? 0) || 0)
        : 0),
    0
  );

  const totalTheoreticalCost = displayItems.reduce(
    (acc, i) =>
      acc +
      (i.include_consumption_in_totals
        ? (Number(
          i.consumption_cost ?? i.theoretical_cost ?? i.basket_cost ?? i.calculated_cost ?? 0
        ) || 0)
        : 0),
    0
  );

  const remainingShoppingTotal = Number(listTotals?.remaining_shopping_total ?? totalBasketCost);
  const originalShoppingTotal = Number(listTotals?.original_shopping_total ?? totalBasketCost);
  const coveredItemsCount = Number(listTotals?.covered_items ?? 0);
  const remainingItemsCount = Number(listTotals?.remaining_items ?? displayItems.length);

  useEffect(() => {
    const variantIds = Array.from(
      new Set(
        (displayItems || [])
          .map((item) => item.product_variant_id)
          .filter(Boolean)
      )
    );

    if (!variantIds.length) {
      setPriceChangesByVariant({});
      return;
    }

    let mounted = true;
    api.price
      .getVariantChanges(variantIds)
      .then((res) => {
        if (!mounted) return;
        const changes = Array.isArray(res?.data?.changes) ? res.data.changes : [];
        setPriceChangesByVariant(
          changes.reduce((acc, change) => {
            acc[String(change.variant_id)] = change;
            return acc;
          }, {})
        );
      })
      .catch(() => {
        if (!mounted) return;
        setPriceChangesByVariant({});
      });

    return () => {
      mounted = false;
    };
  }, [displayItems]);

  useEffect(() => {
    if (!isLoggedIn || !isPremium) {
      setFollowedByGenericId({});
      return;
    }
    let mounted = true;
    api.followedGeneric
      .list()
      .then((res) => {
        if (!mounted) return;
        const next = {};
        for (const item of res?.data?.items || []) {
          if (item?.generic_product_id) next[String(item.generic_product_id)] = item;
        }
        setFollowedByGenericId(next);
      })
      .catch(() => {
        if (mounted) setFollowedByGenericId({});
      });
    return () => {
      mounted = false;
    };
  }, [isLoggedIn, isPremium]);

  useEffect(() => {
    const params = {};
    if (listIdFromQuery) params.list_id = listIdFromQuery;
    if (isGuestFromMealplan) params.source = "meal_plan";

    let mounted = true;
    api.price
      .getSmartBasketSavings(params)
      .then((res) => {
        if (!mounted) return;
        setBasketSavings(res?.data || null);
      })
      .catch(() => {
        if (!mounted) return;
        setBasketSavings(null);
      });

    return () => {
      mounted = false;
    };
  }, [listIdFromQuery, isGuestFromMealplan, rawItems.length]);

  const openHistoryForItem = (item) => {
    if (!item?.generic_product_id) return;
    if (isPremiumBlocked()) {
      openPremiumGate(
        t("premium_required_desc") || "This feature is available only for Premium users."
      );
      return;
    }
    setHistoryItem(item);
  };

  const handleAddToWatchlist = async (item) => {
    if (!item?.product_variant_id) return;
    if (isPremiumBlocked()) {
      openPremiumGate(
        t("follow_price_premium_tease") || "Premium lets you follow products and react to supermarket price changes."
      );
      return;
    }
    const genericKey = String(item.generic_product_id);
    const existing = followedByGenericId[genericKey] || null;
    const previous = followedByGenericId;
    setWatchlistNotice("");
    setFollowedByGenericId((current) => {
      const next = { ...current };
      if (existing) delete next[genericKey];
      else next[genericKey] = { id: `pending-${genericKey}`, generic_product_id: item.generic_product_id };
      return next;
    });
    try {
      if (existing) {
        await api.followedGeneric.unfollow(item.generic_product_id);
      } else {
        const res = await api.followedGeneric.follow({ generic_product_id: item.generic_product_id });
        const followedItem = res?.data?.item;
        setFollowedByGenericId((current) => ({
          ...current,
          [genericKey]: followedItem,
        }));
      }
      setWatchlistNotice(
        existing
          ? (t("follow_removed_success") || "Removed from price watch.")
          : (t("watchlist_added_success") || "Added to your price watchlist.")
      );
    } catch (err) {
      setFollowedByGenericId(previous);
      setWatchlistNotice(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        (t("watchlist_add_error") || "Failed to update watchlist.")
      );
    }
  };

  const handleMembersChange = (e) => {
    const val = Math.max(1, parseInt(e.target.value) || 1);
    setMembers(val);
    if (previewRecipeId) {
      navigate(
        `/${lang}/shopping/list?preview=${previewRecipeId}&members=${val}`
      );
    }
  };

  const handleStoreChange = async (e) => {
    if (needsCountrySelection) {
      if (process.env.REACT_APP_DEBUG_GUEST_STATE === "1") {
        console.info("[ShoppingList] recalculate blocked: country not selected");
      }
      return;
    }
    const value = e.target.value;

    if (!isLoggedIn && !isGuestFromMealplan) {
      setSelectedStore("ALL");
      setError(
        t("store_filter_requires_login") ||
        "Για να επιλέξεις συγκεκριμένο σούπερ μάρκετ ή μάρκα, δημιούργησε έναν δωρεάν λογαριασμό και συνδέσου στην εφαρμογή."
      );
      return;
    }

    setError("");
    selectedStoreChangeSourceRef.current = "handleStoreChange";
    setSelectedStore(value);

    if (!baseItems || baseItems.length === 0) return;

    try {
      setRecalculating(true);
      const resolvedStore = value === "ALL" ? null : value;

      if (previewRecipeId) {
        const itemsPayload = buildRecalcPayloadItems(baseItems);
        const res = await api.shoppingList.recalculate({
          country_id: country,
          lang: apiLang,
          store: resolvedStore,
          store_location_id: resolvedStore,
          list_id: listIdFromQuery || undefined,
          items: itemsPayload,
        });
        const resData = res.data || {};
        const newItems = resData.items || resData.data || [];
        setBaseItems(newItems);
        setRawItems(newItems);
        return;
      }

      const res = await api.shoppingList.updateStoreContext({
        list_id: listIdFromQuery || undefined,
        store_location_id: resolvedStore,
      });
      const payload = res?.data || {};
      const newItems = payload.data || payload.items || [];
      setBaseItems(newItems);
      setRawItems(newItems);
      setCostSnapshot(payload?.list?.cost_snapshot_json || buildSnapshotFromTotals(payload?.totals));
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        t("shopping_list_load_error") ||
        "Σφάλμα κατά την δυναμική ενημέρωση της λίστας."
      );
    } finally {
      setRecalculating(false);
    }
  };

  const selectedGenericIdsForInstacart = useMemo(() => {
    if (!isUS) return [];
    // if preview exists, allow sending selected only; else rawItems selected
    if (selectedIds?.length) return selectedIds;
    // fallback
    return rawItems
      .filter((x) => x.generic_product_id)
      .map((x) => String(x.generic_product_id));
  }, [isUS, selectedIds, rawItems]);



  // ✅ Απόλυτος guest (χωρίς preview / χωρίς guest_mealplan) → hero σελίδα + ads
  if (authChecked && !isLoggedIn && !previewRecipeId && !isGuestFromMealplan && !listIdFromQuery) {

    return (
      <Box
        sx={{ maxWidth: 600, mx: "auto", mt: 6, p: 2, textAlign: "center" }}
      >
        <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            {t("shopping_optimization_empty_title") ||
              "Για βελτιστοποίηση αγορών χρειάζεται πρώτα πλάνο γευμάτων"}
          </Typography>
          <Typography variant="body2">
            {t("shopping_optimization_empty_body") ||
              "Δημιούργησε πρώτα ένα πλάνο γευμάτων για να παραχθεί preview λίστας αγορών με κόστος ανά κατάστημα και βελτιστοποίηση."}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(mealPlanOptimizePath)}
            >
              {t("go_to_meal_plan") || "Πήγαινε στο πλάνο γευμάτων"}
            </Button>
          </Box>
        </Alert>

        {/* <Box sx={{ my: 2 }}>
          <AdSenseAd slot="2080997069" isPremium={isPremium} />
        </Box> */}
      </Box>
    );
  }

  return (
    <Container sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        {/* Τίτλος */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 2,
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          <ShoppingBasketIcon color="primary" sx={{ fontSize: 40 }} />
          <Typography variant="h4" gutterBottom align="center">
            {t("shopping_list_title") || "Shopping List"}
          </Typography>
        </Box>

        {/* TOP banner — μόνο για guests */}
        {/* {!isLoggedIn && (
          <Box sx={{ my: 2 }}>
            <AdSenseAd slot="2080997069" isPremium={isPremium} />
          </Box>
        )} */}

        <Alert
          severity="info"
          icon={<InfoOutlinedIcon fontSize="inherit" />}
          sx={{ mb: 2, fontWeight: 500, borderRadius: 2 }}
        >
          {t("shopping_price_note") ||
            "Οι τιμές είναι εκτιμήσεις ανά κιλό/λίτρο και μπορεί να διαφοροποιούνται ανά κατάστημα."}
        </Alert>
        {costSnapshot?.expected_shopping_total != null ? (
          <Tooltip title={t("snapshot_price_info") || "Prices may have changed since this plan was created."}>
            <Alert
              severity="info"
              icon={<InfoOutlinedIcon fontSize="inherit" />}
              sx={{ mb: 2, fontWeight: 500, borderRadius: 2 }}
            >
              <strong>{t("cost_at_creation") || "Cost at creation"}:</strong>{" "}
              {Number(costSnapshot.expected_shopping_total || 0).toFixed(2)}
              {currencySymbol}
            </Alert>
          </Tooltip>
        ) : null}
        {watchlistNotice ? (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setWatchlistNotice("")}>
            {watchlistNotice}
          </Alert>
        ) : null}
        {basketSavings?.summary?.opportunity_count > 0 ? (
          <Paper
            elevation={0}
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 3,
              border: (theme) => `1px solid ${theme.palette.success.light}`,
              background: "linear-gradient(135deg, rgba(232,245,233,0.95), rgba(255,248,225,0.95))",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
              {t("smart_basket_savings") || "Smart Basket Savings"}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
              {t("shopping_savings_summary") || "Your current basket can be optimized with cheaper market variants."}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {Number(basketSavings.summary.savings_amount || 0).toFixed(2)}
              {basketSavings.summary.currency === "USD" ? "$" : "€"}
              {" · "}
              {Number(basketSavings.summary.savings_pct || 0).toFixed(1)}%
            </Typography>
          </Paper>
        ) : null}

        {/* Preview από συνταγή */}
        {previewRecipeId && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("shopping_list_preview_info") ||
              "Προεπισκόπηση λίστας αγορών για αυτή τη συνταγή. Κάντε εγγραφή για να αποθηκεύσετε και να διαχειριστείτε τη λίστα σας!"}
            <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <Typography sx={{ mr: 1 }}>
                {t("people") || "Άτομα"}:
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                  size="small"
                  onClick={() => {
                    const val = Math.max(1, members - 1);
                    setMembers(val);
                    if (previewRecipeId) {
                      navigate(
                        `/${lang}/shopping/list?preview=${previewRecipeId}&members=${val}`
                      );
                    }
                  }}
                  disabled={members <= 1}
                  aria-label="μείωσε"
                >
                  <RemoveIcon />
                </IconButton>
                <TextField
                  size="small"
                  type="number"
                  value={members}
                  inputProps={{
                    min: 1,
                    max: 20,
                    style: { textAlign: "center" },
                  }}
                  onChange={handleMembersChange}
                  sx={{
                    width: 60,
                    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                    {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                    "& input[type=number]": {
                      MozAppearance: "textfield",
                    },
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    const val = Math.min(20, members + 1);
                    setMembers(val);
                    if (previewRecipeId) {
                      navigate(
                        `/${lang}/shopping/list?preview=${previewRecipeId}&members=${val}`
                      );
                    }
                  }}
                  aria-label="αύξησε"
                >
                  <AddIcon />
                </IconButton>
              </Stack>
            </Box>
          </Alert>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Φίλτρα */}
        <Box
          sx={{
            mb: 2,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Button onClick={() => setShowAll((s) => !s)} variant="outlined">
            {showAll
              ? lang === "el" || lang === "gr"
                ? "Εμφάνιση μόνο προς αγορά"
                : "Show only to buy"
              : lang === "el" || lang === "gr"
                ? "Εμφάνιση όλων"
                : "Show all"}
          </Button>

          {/* Premium teaser για επισκέπτες */}
          {!isLoggedIn && (
            <Alert
              severity="info"
              sx={{
                mb: 2,
                borderRadius: 2,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                flex: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, mb: 0.5 }}
              >
                {t("unlock_premium_features_title") ||
                  "Ξεκλείδωσε επιλογή σούπερ μάρκετ & μαρκών"}
              </Typography>
              <Typography variant="body2">
                {t("unlock_premium_features_desc") ||
                  "Δημιούργησε έναν δωρεάν λογαριασμό για να διαλέγεις συγκεκριμένα σούπερ μάρκετ, να αλλάζεις μάρκα προϊόντων και να αποθηκεύεις τις λίστες σου χωρίς διαφημίσεις."}
              </Typography>
              <Box
                sx={{ mt: 1.5, display: "flex", gap: 1, flexWrap: "wrap" }}
              >
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate(registerPath)}
                >
                  {t("cta_register_button") || "Δωρεάν εγγραφή"}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(loginPath)}
                >
                  {t("login") || "Σύνδεση"}
                </Button>
              </Box>
            </Alert>
          )}

          {/* Επιλογή supermarket */}
          <TextField
            select
            size="small"
            label={t("filter_by_store") || "Φίλτρο καταστήματος"}
            value={selectedStore}
            onChange={handleStoreChange}
            sx={{ minWidth: 220 }}
            disabled={recalculating || !canUsePremiumFeatures}
          >
            <MenuItem value="ALL">
              {t("all_stores") || "Όλα"}
            </MenuItem>
            {availableStores.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>

        </Box>

        {/* BODY */}
        {loading ? (
          <Box sx={{ textAlign: "center", my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : displayItems.length === 0 ? (
          <Alert severity="info">
            {t("shopping_list_empty") || "Δεν υπάρχουν προϊόντα στη λίστα."}
          </Alert>
        ) : usePremiumLayout ? (
          <>
            {/* PREMIUM GRID για συνδεδεμένο χρήστη */}
            <Grid container spacing={2}>
              {rawItems.map((item, idx) => {
                if (item.not_found) {
                  const productLabel =
                    lang === "el" || lang === "gr"
                      ? item.generic_product_name_el ||
                      item.product_name_el ||
                      t("product_not_found_short") ||
                      "Προϊόν"
                      : item.generic_product_name_en ||
                      item.product_name_en ||
                      t("product_not_found_short_en") ||
                      "Product";

                  const qtyForDisplay =
                    item.required_quantity ?? item.qty ?? 0;

                  const storeLabel =
                    selectedStore && selectedStore !== "ALL"
                      ? selectedStore
                      : item.store ||
                      item.store_chain ||
                      item.store_name ||
                      null;

                  return (
                    <Grid
                      item
                      xs={12}
                      sm={8}
                      md={6}
                      lg={4}
                      key={`nf-${idx}`}
                    >
                      <Paper
                        sx={{
                          borderLeft: "4px solid",
                          borderColor: "error.main",
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? theme.palette.error.dark
                              : theme.palette.error.light,
                        }}
                      >
                        <Box sx={{ p: 2 }}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            {productLabel}
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              mt: 0.5,
                              fontWeight: 600,
                              color: (theme) =>
                                theme.palette.mode === "dark"
                                  ? theme.palette.error.contrastText
                                  : theme.palette.error.main,
                            }}
                          >
                            {lang === "el" || lang === "gr" ? (
                              storeLabel ? (
                                <>
                                  Το κατάστημα <b>{storeLabel}</b> δεν έχει
                                  διαθέσιμο προϊόν για αυτό το είδος.
                                </>
                              ) : (
                                "Δεν βρέθηκε διαθέσιμο προϊόν στην περιοχή σας για αυτό το είδος."
                              )
                            ) : storeLabel ? (
                              <>
                                The store <b>{storeLabel}</b> has no available
                                product for this item.
                              </>
                            ) : (
                              "No available product found in your area for this item."
                            )}
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              mt: 0.5,
                              color: "text.secondary",
                              fontStyle: "italic",
                            }}
                          >
                            {lang === "el" || lang === "gr"
                              ? "Απαιτούμενη ποσότητα:"
                              : "Required quantity:"}{" "}
                            <b>
                              {formatQuantity(
                                qtyForDisplay,
                                country,
                                item.required_unit || "g",
                                lang
                              )}
                            </b>
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                }

                return (
                  <ShoppingListItemRow
                    key={item.id || idx}
                    item={item}
                    onUpdate={refreshCurrentData}
                    onItemUpdated={(updatedItem) => {
                      if (!updatedItem) return;
                      const applyUpdate = (current) =>
                        current.map((existing) =>
                          String(existing.id) === String(updatedItem.id) ? updatedItem : existing
                        );
                      setRawItems((current) => applyUpdate(current));
                      setBaseItems((current) => applyUpdate(current));
                    }}
                    readOnly={readOnly}
                    canChangeSelection={canChangeSelection}
                    onGate={openGateForQuantityUpdate}
                    priceChange={priceChangesByVariant[String(item.product_variant_id)] || null}
                    onOpenHistory={openHistoryForItem}
                    onAddToWatchlist={handleAddToWatchlist}
                    canAddToWatchlist={Boolean(item.generic_product_id)}
                    isFollowed={Boolean(followedByGenericId[String(item.generic_product_id)])}
                  />


                );
              })}
            </Grid>

            <Box sx={{ my: 2 }}>
              <Typography>
                <b>
                  {lang === "el" || lang === "gr"
                    ? "Κόστος πλάνου γευμάτων (θεωρητικό)"
                    : "Meal plan cost (theoretical)"}
                  :
                </b>{" "}
                {totalTheoreticalCost.toFixed(2)}
                {currencySymbol}
              </Typography>
              <Typography>
                <b>
                  {lang === "el" || lang === "gr"
                    ? "Αρχικό σύνολο αγορών"
                    : "Original shopping total"}
                  :
                </b>{" "}
                {originalShoppingTotal.toFixed(2)}
                {currencySymbol}
              </Typography>
              <Typography>
                <b>
                  {lang === "el" || lang === "gr"
                    ? "Υπόλοιπο αγορών"
                    : "Remaining shopping total"}
                  :
                </b>{" "}
                {remainingShoppingTotal.toFixed(2)}
                {currencySymbol}
              </Typography>
              <Typography>
                <b>{lang === "el" || lang === "gr" ? "Καλυμμένα είδη" : "Covered items"}:</b>{" "}
                {coveredItemsCount}
              </Typography>
              <Typography>
                <b>{lang === "el" || lang === "gr" ? "Είδη που απομένουν" : "Remaining items"}:</b>{" "}
                {remainingItemsCount}
              </Typography>
              {missingPricingCount > 0 ? (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  {lang === "el" || lang === "gr"
                    ? `Ελλιπή τιμολόγηση σε ${missingPricingCount} προϊόντα. Τα σύνολα εξαιρούν αυτά τα είδη όπου λείπουν δεδομένα.`
                    : `Incomplete pricing for ${missingPricingCount} items. Totals exclude items with missing pricing data.`}
                </Alert>
              ) : null}
            </Box>
            {/* ✅ Instacart checkout και για saved λίστα (μόνο US) */}
            {isUS && (
              <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
                <InstacartCheckoutButton
                  context="custom_list"
                  selectedGenericIds={selectedGenericIdsForInstacart}
                  lang={lang}
                  items={rawItems
                    .filter((it) => it.generic_product_id && selectedIds.includes(String(it.generic_product_id)))
                    .map((it) => ({
                      generic_product_id: Number(it.generic_product_id),
                      qty: Number(it.required_quantity ?? it.needQty ?? it.qty ?? it.displayQty ?? 0),
                      unit: it.unit || "g",
                    }))
                    .filter((it) => it.qty > 0)}
                />
              </Box>
            )}

          </>
        ) : (
          // 🔹 Preview / guest (List + Paper + checkboxes για Instacart)
          <>
            <List>
              {displayItems.map((item, idx) => {
                const gpId = item.generic_product_id;
                const isSelected =
                  gpId && selectedIds.includes(String(gpId));

                const productLabel =
                  lang === "el" || lang === "gr"
                    ? item.product_name_el || item.name_el || item.name
                    : item.product_name_en || item.name_en || item.name;

                const genericLabel =
                  lang === "el" || lang === "gr"
                    ? item.generic_product_name_el || ""
                    : item.generic_product_name_en || "";

                const storeChain = item.store_chain || "";
                const storeName = item.store_name || "";
                const packageSizeText =
                  item.package_size_text ||
                  item.pricing_snapshot?.package_size_text ||
                  item.selected_variant_snapshot?.package_size_text ||
                  null;
                const packagePrice =
                  item.package_price ??
                  item.pricing_snapshot?.package_price ??
                  item.price ??
                  null;
                const storeLabelRaw =
                  item.store ||
                  (storeChain && storeName && storeChain !== storeName
                    ? `${storeChain} – ${storeName}`
                    : storeChain || storeName || "");

                const qtyLabel = formatQuantity(
                  item.displayQty ?? item.required_qty ?? item.required_quantity ?? item.qty ?? 0,
                  country,
                  item.required_unit || item.base_unit || "g",
                  lang
                );

                const effectiveUnitKind =
                  (item.unit_kind || item.unit_price_kind || item.normalized_unit || "").toLowerCase();
                const isLiquid =
                  effectiveUnitKind === "l" || effectiveUnitKind === "ml";
                const isPiece =
                  effectiveUnitKind === "piece";

                const unitPriceSource =
                  item.unit_price ??
                  item.price_per_piece ??
                  item.price_per_kg ??
                  item.price_per_liter ??
                  item.price_per_kilo ??
                  null;

                const unitPriceLabel = unitPriceSource
                  ? formatUnitPriceForCountry(
                    unitPriceSource,
                    country,
                    isLiquid,
                    isPiece,
                    currencySymbol
                  )
                  : `-- ${currencySymbol}`;

                const theoreticalCost = Number(
                  item.theoretical_cost ??
                  item.calculated_cost ??
                  item.total_cost ??
                  0
                );

                const basketCost = Number(
                  item.basket_cost ?? theoreticalCost ?? 0
                );

                return (
                  <Paper
                    key={item.id || idx}
                    sx={{
                      mb: 1,
                      borderRadius: 2,
                      border: isSelected
                        ? (theme) =>
                          `2px solid ${theme.palette.success.main}`
                        : (theme) =>
                          `1px dashed ${theme.palette.divider}`,
                      backgroundColor: isSelected
                        ? (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(46, 125, 50, 0.15)"
                            : "rgba(200, 230, 201, 0.6)"
                        : (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "#fafafa",
                      boxShadow: isSelected ? 3 : 0,
                      transform: isSelected ? "translateY(-1px)" : "none",
                      transition: "all 0.18s ease-in-out",
                      "&:hover": {
                        boxShadow: isSelected ? 4 : 2,
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Box sx={{ p: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 2,
                          mb: 0.5,
                        }}
                      >
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                          <Checkbox
                            size="small"
                            checked={!!isSelected}
                            onChange={() =>
                              gpId && toggleSelected(gpId)
                            }
                          />
                          <Box>
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                            >
                              {productLabel}
                            </Typography>
                            {genericLabel && (
                              <Typography
                                variant="body2"
                                sx={{ color: "text.secondary" }}
                              >
                                {t("generic_product") ||
                                  "Γενικό προϊόν"}
                                : <b>{genericLabel}</b>
                              </Typography>
                            )}
                            {packageSizeText || packagePrice != null ? (
                              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                {[packagePrice != null ? `${Number(packagePrice).toFixed(2)}${currencySymbol}` : null, packageSizeText, unitPriceLabel !== `-- ${currencySymbol}` ? unitPriceLabel : null]
                                  .filter(Boolean)
                                  .join(" • ")}
                              </Typography>
                            ) : null}

                            {storeLabelRaw && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  mt: 0.5,
                                  gap: 1,
                                }}
                              >
                                <StorefrontIcon
                                  sx={{
                                    fontSize: 18,
                                    color: "primary.main",
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    color: "primary.main",
                                  }}
                                >
                                  {storeLabelRaw}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        <Box sx={{ textAlign: "right" }}>
                          <Typography variant="body2">
                            {t("qty") || "Ποσότητα"}: <b>{qtyLabel}</b>
                          </Typography>
                          {priceChangesByVariant[String(item.product_variant_id)]?.delta != null ? (
                            <Typography
                              variant="body2"
                              sx={{
                                color:
                                  Number(priceChangesByVariant[String(item.product_variant_id)]?.delta || 0) < 0
                                    ? "success.main"
                                    : "warning.main",
                              }}
                            >
                              {Number(priceChangesByVariant[String(item.product_variant_id)]?.delta || 0) < 0
                                ? (t("price_drop") || "Price drop")
                                : (t("price_increase") || "Price increase")}
                              {priceChangesByVariant[String(item.product_variant_id)]?.delta_pct != null
                                ? ` (${Number(priceChangesByVariant[String(item.product_variant_id)]?.delta_pct).toFixed(1)}%)`
                                : ""}
                            </Typography>
                          ) : null}
                          <Typography variant="body2">
                            {t("unit_price") || "Τιμή ανά μονάδα"} : <b>{unitPriceLabel}</b>
                          </Typography>

                          <Typography variant="body2">
                            {t("cost_for_qty") || "Κόστος ποσότητας (θεωρητικό)"} :{" "}
                            <b>
                              {theoreticalCost.toFixed(2)}
                              {currencySymbol}
                            </b>
                          </Typography>

                          <Typography variant="body2">
                            {t("basket_cost") || "Πραγματικό κόστος καλαθιού"} :{" "}
                            <b>
                              {basketCost.toFixed(2)}
                              {currencySymbol}
                            </b>
                          </Typography>
                          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <Button size="small" onClick={() => openHistoryForItem(item)}>
                              {t("price_history") || "History"}
                            </Button>
                          </Stack>

                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </List>
            <Box sx={{ my: 2 }}>
              <Typography>
                <b>
                  {lang === "el" || lang === "gr"
                    ? "Κόστος πλάνου γευμάτων (θεωρητικό)"
                    : "Meal plan cost (theoretical)"}
                  :
                </b>{" "}
                {totalTheoreticalCost.toFixed(2)}
                {currencySymbol}
              </Typography>

              <Typography>
                <b>
                  {lang === "el" || lang === "gr"
                    ? "Αρχικό σύνολο αγορών"
                    : "Original shopping total"}
                  :
                </b>{" "}
                {originalShoppingTotal.toFixed(2)}
                {currencySymbol}
              </Typography>
              <Typography>
                <b>
                  {lang === "el" || lang === "gr"
                    ? "Υπόλοιπο αγορών"
                    : "Remaining shopping total"}
                  :
                </b>{" "}
                {remainingShoppingTotal.toFixed(2)}
                {currencySymbol}
              </Typography>

              {isUS && (
                <Box sx={{ mt: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700 }}
                  >
                    {t("us_estimate_note_title", "Σημείωση (ΗΠΑ)")}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ opacity: 0.85 }}
                  >
                    {t(
                      "us_retailer_provider_estimate_note",
                      "This is an InCooking estimate from retailer provider data in your shopping area. Instacart checkout totals and availability may differ."
                    )}
                  </Typography>
                </Box>
              )}
            </Box>


            {/* Instacart checkout μόνο για US + preview */}
            {isUS && selectedIds.length > 0 && (
              <Box sx={{ mt: 2 }}>
                {canUsePremiumFeatures ? (
                  <InstacartCheckoutButton
                    selectedGenericIds={selectedIds}
                    context={isUserListMode ? "saved_list" : "recipe"}
                    recipeId={previewRecipeId ? Number(previewRecipeId) : null}
                    servings={members}
                  />
                ) : (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<ShoppingBasketIcon />}
                    onClick={() => openPremiumGate(t("premium_required_desc") || "This feature is available only for Premium users.")}
                    sx={{ fontWeight: 800, borderRadius: 2 }}
                  >
                    {t("checkout_instacart") || "Checkout with Instacart"}
                  </Button>
                )}
              </Box>
            )}


          </>
        )}

        {/* BOTTOM banner — μόνο για guests */}
        {/* {!isLoggedIn && (
          <Box sx={{ mt: 3 }}>
            <AdSenseAd slot="2080997069" isPremium={isPremium} />
          </Box>
        )} */}
      </Paper>
      <FeatureGateDialog
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        lang={lang}
        isLoggedIn={isLoggedIn}
        require={gateRequire}
        title={t("premium_feature_title") || "Premium λειτουργία"}
        message={gateMessage}
        onLogin={() => { setGateOpen(false); navigate(loginPath); }}
        onRegister={() => { setGateOpen(false); navigate(registerPath); }}
        onUpgrade={() => { setGateOpen(false); navigate(plansPath); }}
      />
      <PriceHistoryDialog
        open={Boolean(historyItem)}
        onClose={() => setHistoryItem(null)}
        variantId={historyItem?.product_variant_id}
        itemLabel={historyItem?.product_name_el || historyItem?.product_name_en || historyItem?.name}
        user={isLoggedIn ? { entitlements } : null}
      />

    </Container>
  );
}
